import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  ListItem,
  ListItemText,
  List,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Switch,
  Tooltip,
  Badge,
  Avatar,
  LinearProgress,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build,
  CheckCircle,
  Warning,
  Error,
  Info,
  Schedule,
  Person,
  Assignment,
  PriorityHigh,
  LowPriority,
  ExpandMore,
  FilterList,
  Refresh,
  Print,
  Download,
  Visibility,
  Work,
  Engineering,
  Timeline,
  Assessment,
  Notifications,
} from '@mui/icons-material';
import { format, parseISO, addDays, isAfter, isBefore } from 'date-fns';
import { useRbac } from '../../context/RbacContext';
import {
  useGetMaintenanceRequestsQuery,
  useCreateMaintenanceRequestMutation,
  useUpdateMaintenanceRequestMutation,
  useDeleteMaintenanceRequestMutation,
} from '../../api/maintenanceApi';

import { MaintenanceRequest, MaintenanceResponse, MaintenancePriority, MaintenanceStatus, MaintenanceType } from '../../types/maintenance';



interface MaintenanceSchedule {
  id: string;
  machineId: string;
  type: MaintenanceType;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  lastMaintenance: string;
  nextMaintenance: string;
  assignedTechnician: string;
  status: 'active' | 'inactive';
}

interface MaintenanceHistory {
  id: string;
  requestId: string;
  action: string;
  performedBy: string;
  timestamp: string;
  notes: string;
}

export const MaintenanceManagement: React.FC = () => {
  const { canManage } = useRbac();
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [newRequest, setNewRequest] = useState<Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt'>>({
    title: '',
    description: '',
    machineId: '',
    type: 'corrective' as MaintenanceType,
    priority: 'medium' as MaintenancePriority,
    status: 'pending' as MaintenanceStatus,
    scheduledDate: undefined,
    assignedTechnicianId: undefined,
    notes: '',
    resolution: '',
    completedDate: undefined,
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<MaintenanceResponse | null>(null);
  const [filter, setFilter] = useState({
    status: 'all',
    priority: 'all',
    type: 'all',
    assignedTo: 'all'
  });
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [history, setHistory] = useState<MaintenanceHistory[]>([]);

  const { data: requests, isLoading: isFetching, error: fetchError, refetch } = useGetMaintenanceRequestsQuery();
  const [createRequest, { isLoading: isCreating }] = useCreateMaintenanceRequestMutation();
  const [updateRequest, { isLoading: isUpdating }] = useUpdateMaintenanceRequestMutation();
  const [deleteRequest, { isLoading: isDeleting }] = useDeleteMaintenanceRequestMutation();

  useEffect(() => {
    if (fetchError) {
      setError('Failed to fetch maintenance requests');
    }
  }, [fetchError]);

  // Mock data for schedules and history
  useEffect(() => {
    setSchedules([
      {
        id: '1',
        machineId: 'MACH-001',
        type: 'preventive',
        frequency: 'monthly',
        lastMaintenance: '2024-01-15',
        nextMaintenance: '2024-02-15',
        assignedTechnician: 'John Doe',
        status: 'active'
      },
      {
        id: '2',
        machineId: 'MACH-002',
        type: 'routine',
        frequency: 'weekly',
        lastMaintenance: '2024-01-20',
        nextMaintenance: '2024-01-27',
        assignedTechnician: 'Jane Smith',
        status: 'active'
      }
    ]);

    setHistory([
      {
        id: '1',
        requestId: 'REQ-001',
        action: 'Maintenance started',
        performedBy: 'John Doe',
        timestamp: '2024-01-25T10:00:00Z',
        notes: 'Initial inspection completed'
      },
      {
        id: '2',
        requestId: 'REQ-001',
        action: 'Parts replaced',
        performedBy: 'John Doe',
        timestamp: '2024-01-25T14:30:00Z',
        notes: 'Replaced worn bearings'
      }
    ]);
  }, []);

  const handleCreateRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      const request: Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt'> = {
        title: newRequest.title || 'New Maintenance Request',
        description: newRequest.description || '',
        machineId: newRequest.machineId || '',
        type: newRequest.type || 'corrective',
        priority: newRequest.priority || 'medium',
        status: newRequest.status || 'pending' as MaintenanceStatus,
        scheduledDate: newRequest.scheduledDate || undefined,
        assignedTechnicianId: newRequest.assignedTechnicianId || undefined,
        notes: newRequest.notes || '',
        resolution: newRequest.resolution || '',
        completedDate: newRequest.completedDate || undefined,
      };

      await createRequest(request).unwrap();
      setOpen(false);
      setNewRequest({
        title: '',
        description: '',
        machineId: '',
        type: 'corrective' as MaintenanceType,
        priority: 'medium' as MaintenancePriority,
        status: 'pending',
        scheduledDate: undefined,
        assignedTechnicianId: undefined,
        notes: '',
        resolution: '',
        completedDate: undefined,
      });
      refetch();
    } catch (err) {
      setError('Failed to create maintenance request');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRequest = async (id: string, status: MaintenanceStatus) => {
    try {
      await updateRequest({
        id,
        data: {
          id: selectedRequest?.id || '',
          title: selectedRequest?.title || '',
          description: selectedRequest?.description || '',
          machineId: selectedRequest?.machineId || '',
          type: selectedRequest?.type || 'corrective',
          priority: selectedRequest?.priority || 'medium',
          status: status as MaintenanceStatus,
          scheduledDate: selectedRequest?.scheduledDate || undefined,
          assignedTechnicianId: selectedRequest?.assignedTechnicianId || undefined,
          notes: selectedRequest?.notes || '',
          resolution: selectedRequest?.resolution || '',
          completedDate: selectedRequest?.completedDate || undefined,
          createdAt: selectedRequest?.createdAt || '',
          updatedAt: selectedRequest?.updatedAt || '',
        }
      }).unwrap();
      refetch();
    } catch (err) {
      setError('Failed to update maintenance request');
    }
  };

  const handleDeleteRequest = async (id: string) => {
    if (!canManage('admin:maintenance')) {
      setError('You do not have permission to delete maintenance requests');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this maintenance request?')) {
      return;
    }

    try {
      await deleteRequest(id).unwrap();
      refetch();
    } catch (err) {
      setError('Failed to delete maintenance request');
    }
  };

  const handleEditRequest = (request: MaintenanceResponse) => {
    setSelectedRequest(request);
    setOpen(true);
  };

  const renderPriorityChip = (priority: MaintenancePriority) => {
    switch (priority) {
      case 'critical':
        return <Chip label="Critical" color="error" size="small" icon={<PriorityHigh />} />;
      case 'high':
        return <Chip label="High" color="error" size="small" />;
      case 'medium':
        return <Chip label="Medium" color="warning" size="small" />;
      case 'low':
        return <Chip label="Low" color="success" size="small" icon={<LowPriority />} />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  const renderStatusChip = (status: MaintenanceStatus) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;

      case 'in_progress':
        return <Chip label="In Progress" color="info" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" size="small" />;

      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  const renderTypeChip = (type: MaintenanceType) => {
    switch (type) {
      case 'preventive':
        return <Chip label="Preventive" color="primary" size="small" />;
      case 'corrective':
        return <Chip label="Corrective" color="secondary" size="small" />;
      case 'emergency':
        return <Chip label="Emergency" color="error" size="small" />;
      case 'routine':
        return <Chip label="Routine" color="success" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  const filteredRequests = requests?.filter(request => {
    if (filter.status !== 'all' && request.status !== filter.status) return false;
    if (filter.priority !== 'all' && request.priority !== filter.priority) return false;
    if (filter.type !== 'all' && request.type !== filter.type) return false;
    return true;
  }) || [];

  const getOverdueRequests = () => {
    return requests?.filter(request => 
      request.scheduledDate && isBefore(parseISO(request.scheduledDate), new Date()) && request.status !== 'completed'
    ) || [];
  };

  const getUpcomingSchedules = () => {
    return schedules.filter(schedule => 
      isAfter(parseISO(schedule.nextMaintenance), new Date()) && 
      isBefore(parseISO(schedule.nextMaintenance), addDays(new Date(), 7))
    );
  };

  const renderMachineDetails = () => {
    if (!selectedRequest) return null;

    return (
      <List>
        <ListItem>
          <ListItemText
            primary="Machine ID"
            secondary={selectedRequest.machineId}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Priority"
            secondary={renderPriorityChip(selectedRequest.priority)}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Status"
            secondary={renderStatusChip(selectedRequest.status)}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Type"
            secondary={renderTypeChip(selectedRequest.type)}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Created At"
            secondary={format(parseISO(selectedRequest.createdAt), 'PPP')}
          />
        </ListItem>
        {selectedRequest.updatedAt && (
          <ListItem>
            <ListItemText
              primary="Updated At"
              secondary={format(parseISO(selectedRequest.updatedAt), 'PPP')}
            />
          </ListItem>
        )}
      </List>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Build color="primary" />
        Maintenance Management
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Requests
              </Typography>
              <Typography variant="h4">
                {requests?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {requests?.filter(r => r.status === 'pending').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" color="info.main">
                {requests?.filter(r => r.status === 'in_progress').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Overdue
              </Typography>
              <Typography variant="h4" color="error.main">
                {getOverdueRequests().length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Requests" />
          <Tab label="Schedules" />
          <Tab label="History" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      {selectedTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              disabled={!canManage('manager:maintenance') && !canManage('admin:maintenance')}
            >
              New Request
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filter.status}
                  onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filter.priority}
                  onChange={(e) => setFilter(prev => ({ ...prev, priority: e.target.value }))}
                  label="Priority"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={() => refetch()}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          {isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredRequests.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Machine</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {request.id.slice(0, 8)}…
                        </Typography>
                      </TableCell>
                      <TableCell>{request.machineId || 'N/A'}</TableCell>
                      <TableCell>{renderTypeChip(request.type || 'corrective')}</TableCell>
                      <TableCell>{renderStatusChip(request.status)}</TableCell>
                      <TableCell>{renderPriorityChip(request.priority)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            <Person />
                          </Avatar>
                          {request.assignedTechnicianId || 'Unassigned'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {request.scheduledDate ? format(parseISO(request.scheduledDate), 'MMM dd, yyyy') : 'Not set'}
                      </TableCell>
                      <TableCell>{format(parseISO(request.createdAt), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleEditRequest(request)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditRequest(request)}
                              disabled={!canManage('manager:maintenance') && !canManage('admin:maintenance')}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRequest(request.id)}
                              disabled={!canManage('admin:maintenance')}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                No maintenance requests found
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ mb: 2 }}
                              disabled={!canManage('manager:maintenance') && !canManage('admin:maintenance')}
          >
            Create Schedule
          </Button>
          <Grid container spacing={2}>
            {schedules.map((schedule) => (
              <Grid item xs={12} md={6} key={schedule.id}>
                <Card>
                  <CardHeader
                    title={`Machine ${schedule.machineId}`}
                    subheader={`${schedule.type} - ${schedule.frequency}`}
                    action={
                      <Chip
                        label={schedule.status}
                        color={schedule.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Last Maintenance: {format(parseISO(schedule.lastMaintenance), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Next Maintenance: {format(parseISO(schedule.nextMaintenance), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Assigned: {schedule.assignedTechnician}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Maintenance History
          </Typography>
          <List>
            {history.map((item) => (
              <ListItem key={item.id}>
                <ListItemText
                  primary={item.action}
                  secondary={`${item.performedBy} - ${format(parseISO(item.timestamp), 'MMM dd, yyyy HH:mm')}`}
                />
                <Typography variant="body2" color="textSecondary">
                  {item.notes}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {selectedTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Maintenance Analytics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Request Status Distribution" />
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {requests?.filter(r => r.status === 'completed').length || 0} / {requests?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Completion Rate
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={requests?.length ? (requests.filter(r => r.status === 'completed').length / requests.length) * 100 : 0}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Upcoming Schedules" />
                <CardContent>
                  {getUpcomingSchedules().map((schedule) => (
                    <Box key={schedule.id} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {schedule.machineId} - {format(parseISO(schedule.nextMaintenance), 'MMM dd')}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedRequest ? 'Edit Maintenance Request' : 'New Maintenance Request'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Machine ID"
                value={newRequest.machineId}
                onChange={(e) => setNewRequest(prev => ({ ...prev, machineId: e.target.value }))}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select
                  value={newRequest.type}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, type: e.target.value as MaintenanceType }))}
                  label="Type"
                >
                  <MenuItem value="preventive">Preventive</MenuItem>
                  <MenuItem value="corrective">Corrective</MenuItem>
                  <MenuItem value="emergency">Emergency</MenuItem>
                  <MenuItem value="routine">Routine</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newRequest.priority}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, priority: e.target.value as MaintenancePriority }))}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={newRequest.scheduledDate ? newRequest.scheduledDate.split('T')[0] : ''}
                onChange={(e) => setNewRequest(prev => ({ ...prev, scheduledDate: e.target.value }))}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newRequest.description}
                onChange={(e) => setNewRequest(prev => ({ ...prev, description: e.target.value }))}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={newRequest.notes}
                onChange={(e) => setNewRequest(prev => ({ ...prev, notes: e.target.value }))}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateRequest}
            variant="contained"
            disabled={loading || (!canManage('manager:maintenance') && !canManage('admin:maintenance'))}
          >
            {loading ? <CircularProgress size={20} /> : 'Create Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
